import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Pie, Doughnut } from 'react-chartjs-2';
import { ChartData } from '../types';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface ChartComponentProps {
  data: ChartData;
}

export interface ChartComponentHandle {
  exportAsImage: (filename: string) => void;
}

const ChartComponent = forwardRef<ChartComponentHandle, ChartComponentProps>(({ data }, ref) => {
  const chartRef = useRef<any>(null);

  useImperativeHandle(ref, () => ({
    exportAsImage: (filename: string) => {
      if (chartRef.current) {
        const canvas = chartRef.current.canvas;
        if (canvas) {
          const url = canvas.toDataURL('image/png');
          const link = document.createElement('a');
          link.download = filename;
          link.href = url;
          link.click();
        }
      }
    },
  }));

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            family: 'neuro-silkafont-regular, sans-serif',
          },
          color: '#0A2C40',
        },
      },
      tooltip: {
        backgroundColor: '#0A2C40',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        borderColor: '#E2E8F0',
        borderWidth: 1,
        font: {
          family: 'neuro-silkafont-regular, sans-serif',
        },
      },
    },
    scales: data.chartType !== 'pie' && data.chartType !== 'doughnut' ? {
      x: {
        ticks: {
          font: {
            family: 'neuro-silkafont-regular, sans-serif',
          },
          color: '#64748B',
        },
        grid: {
          color: '#E2E8F0',
        },
      },
      y: {
        ticks: {
          font: {
            family: 'neuro-silkafont-regular, sans-serif',
          },
          color: '#64748B',
        },
        grid: {
          color: '#E2E8F0',
        },
      },
    } : undefined,
  };

  const chartData = {
    labels: data.labels,
    datasets: data.datasets.map((dataset, idx) => ({
      ...dataset,
      backgroundColor: dataset.backgroundColor || generateColors(data.labels.length, idx),
      borderColor: dataset.borderColor || generateBorderColors(data.labels.length, idx),
      borderWidth: dataset.borderWidth || 2,
    })),
  };

  const renderChart = () => {
    switch (data.chartType) {
      case 'line':
        return <Line ref={chartRef} options={options} data={chartData} />;
      case 'pie':
        return <Pie ref={chartRef} options={options} data={chartData} />;
      case 'doughnut':
        return <Doughnut ref={chartRef} options={options} data={chartData} />;
      case 'bar':
      default:
        return <Bar ref={chartRef} options={options} data={chartData} />;
    }
  };

  return <div style={{ maxWidth: '100%', maxHeight: '400px' }}>{renderChart()}</div>;
});

// Helper function to generate colors based on Newton design system
function generateColors(count: number, datasetIndex: number): string | string[] {
  const primaryColors = [
    'rgba(10, 44, 64, 0.8)',      // Primary navy
    'rgba(0, 143, 219, 0.8)',     // Teal
    'rgba(16, 185, 129, 0.8)',    // Success green
    'rgba(59, 130, 246, 0.8)',    // Info blue
    'rgba(245, 158, 11, 0.8)',    // Warning orange
    'rgba(239, 68, 68, 0.8)',     // Danger red
  ];

  if (count === 1) {
    return primaryColors[datasetIndex % primaryColors.length];
  }

  return Array(count)
    .fill(0)
    .map((_, idx) => primaryColors[(idx + datasetIndex) % primaryColors.length]);
}

function generateBorderColors(count: number, datasetIndex: number): string | string[] {
  const borderColors = [
    '#0A2C40',      // Primary navy
    '#008FDB',      // Teal
    '#10B981',      // Success green
    '#3B82F6',      // Info blue
    '#F59E0B',      // Warning orange
    '#EF4444',      // Danger red
  ];

  if (count === 1) {
    return borderColors[datasetIndex % borderColors.length];
  }

  return Array(count)
    .fill(0)
    .map((_, idx) => borderColors[(idx + datasetIndex) % borderColors.length]);
}

export default ChartComponent;
